/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import {
  CHILD_COUNT_LABEL_KEY,
  childCountKind,
} from '@sportninja/common/features/competition/operations/utils/childCountKind'
import { canDragNode } from '@sportninja/common/features/competition/operations/utils/canDropNode'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import {
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import { nodeTypePalette } from '../../utils/nodeTypePalette'
import DragHandle, { useSortableRow } from '../dnd/DragHandle'
import TreeNodeActions from './TreeNodeActions'

interface TreeNodeRowProps {
  node: OperationsNode
  depth: number
  isSelected: boolean
  isExpanded: boolean
  isMatch: boolean
  hasChildren: boolean
  canEdit: boolean
  isRegistrationOn?: boolean
  /** True while a save is in flight for this subtree. */
  isDragDisabled?: boolean
  /** How this row would receive the team currently being dragged. */
  dropHint?: 'before' | 'after' | 'into' | null
  onSelect: (id: string) => void
  onToggle: (id: string) => void
  onAdd: (node: OperationsNode, kind: 'division' | 'team') => void
  onEdit: (node: OperationsNode) => void
  onDelete: (node: OperationsNode) => void
}

const translate = t as (key: string, options?: unknown) => string

const TYPE_BADGE: Record<string, string> = {
  season: 'S',
  conference: 'C',
  division: 'D',
}

/** Secondary line: what the node is, plus the counts the payload gives us. */
const metaFor = (node: OperationsNode): string => {
  if (isTeamNode(node)) {
    return `Team · ${node.gamesCount} games`
  }
  const label = node.type.charAt(0).toUpperCase() + node.type.slice(1)
  const childCount = node.children.length
  // See `childCountKind`: an empty division must not read "0 divisions", and a
  // season of conferences must not read "divisions".
  const childLabel = translate(CHILD_COUNT_LABEL_KEY[childCountKind(node)])
  return `${label} · ${childCount} ${childLabel}`
}

const TreeNodeRow = ({
  node,
  depth,
  isSelected,
  isExpanded,
  isMatch,
  hasChildren,
  canEdit,
  isRegistrationOn,
  isDragDisabled,
  dropHint,
  onSelect,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
}: TreeNodeRowProps) => {
  'use memo'

  const badge = isTeamNode(node)
    ? node.seed === null
      ? '–'
      : String(node.seed)
    : TYPE_BADGE[node.type] ?? 'D'

  // A team shows its crest in place of the seed badge. `logoFailed` covers the
  // URL that resolves but 404s, which would otherwise leave an empty circle.
  const [logoFailed, setLogoFailed] = useState(false)
  const logoUrl = isTeamNode(node) ? resolveAssetUrl(node.image) : undefined
  const showLogo = Boolean(logoUrl) && !logoFailed

  const draggable = canDragNode(node, canEdit) && !isDragDisabled
  const sortable = useSortableRow(node.id, !draggable)

  return (
    <div
      ref={sortable.setNodeRef}
      style={sortable.style}
      css={[rowStyle(depth, isSelected), dropHintStyle(dropHint)]}
      className='tree-row'
    >
      <DragHandle
        disabled={!draggable}
        label={`Reorder ${node.name}`}
        setActivatorNodeRef={sortable.setActivatorNodeRef}
        // Spread into a plain object: dnd-kit's `DraggableAttributes` is a
        // closed interface, which TS will not widen to an index signature.
        attributes={{ ...sortable.attributes }}
        listeners={sortable.listeners}
      />

      {hasChildren ? (
        <button
          type='button'
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
          css={twistyStyle}
          onClick={(event) => {
            event.stopPropagation()
            onToggle(node.id)
          }}
        >
          {isExpanded ? '▾' : '▸'}
        </button>
      ) : (
        <span css={twistyStyle} aria-hidden />
      )}

      {showLogo ? (
        <img
          src={logoUrl}
          alt=''
          css={logoStyle(isMatch)}
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span css={badgeStyle(node.type, isMatch)} aria-hidden>
          {badge}
        </span>
      )}

      <button
        type='button'
        css={labelButton}
        aria-current={isSelected ? 'true' : undefined}
        onClick={() => onSelect(node.id)}
      >
        <span css={nameStyle(isSelected, isMatch)}>{node.name}</span>
        <span css={metaStyle}>{metaFor(node)}</span>
      </button>

      <TreeNodeActions
        node={node}
        canEdit={canEdit}
        isRegistrationOn={isRegistrationOn}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  )
}

/**
 * Where the dragged team would land.
 *
 * Rows no longer shift to preview the drop (the rail is a tree, not one flat
 * sortable list — see `OperationsTree`), so without this the only feedback was
 * the row lifting, and it was guesswork whether a drop near a list edge would
 * register. `into` fills a whole division row; `before`/`after` draw the exact
 * insertion line.
 *
 * Uses `::after` because `rowStyle`'s `::before` is the selection bar.
 */
const dropHintStyle = (
  hint: 'before' | 'after' | 'into' | null | undefined
) => {
  if (!hint) {
    return css``
  }

  if (hint === 'into') {
    return css`
      background: ${colors.PRIMARY}1f;
      box-shadow: inset 0 0 0 1px ${colors.PRIMARY};
      border-radius: 4px;
    `
  }

  return css`
    &::after {
      content: '';
      position: absolute;
      left: 8px;
      right: 8px;
      height: 2px;
      border-radius: 1px;
      background: ${colors.PRIMARY};
      ${hint === 'before' ? 'top: -1px;' : 'bottom: -1px;'}
      pointer-events: none;
    }
  `
}

const rowStyle = (depth: number, isSelected: boolean) => css`
  display: flex;
  /* Top-aligned, not centred: the label block is two lines (name + meta) while
     the controls beside it are single glyphs, so centring floated them against
     the middle of the block instead of the name they belong to. */
  align-items: flex-start;
  gap: 6px;
  min-height: 32px;
  padding: 5px 12px 5px ${3 + depth * 16}px;
  position: relative;
  border-top: 1px solid rgba(50, 58, 72, 0.4);

  /* The rail already has the search bar's bottom border above it — a second
     line on the first row reads as a double rule. */
  &:first-of-type {
    border-top: none;
  }
  background: ${isSelected ? `${colors.PRIMARY}24` : 'transparent'};

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${isSelected ? colors.PRIMARY : 'transparent'};
  }

  &:hover {
    background: ${isSelected
      ? `${colors.PRIMARY}24`
      : 'rgba(255, 255, 255, 0.028)'};
  }

  .tree-row-actions {
    opacity: ${isSelected ? 1 : 0};
  }

  &:hover .tree-row-actions,
  &:focus-within .tree-row-actions {
    opacity: 1;
  }
`

/* 20px tall to match the badge, so the glyph centres on the NAME line rather
   than pinning to the top of the row now that rows are top-aligned. */
const twistyStyle = css`
  width: 18px;
  height: 20px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: ${colors.NEUTRAL_100};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }
`

const badgeStyle = (type: string, isMatch: boolean) => {
  const palette = nodeTypePalette(type)

  return css`
    ${weightSemiBold}
    width: 20px;
    height: 20px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    border-radius: ${type === 'team' ? '50%' : '5px'};
    color: ${palette.fg};
    background: ${palette.bg};
    box-shadow: ${isMatch ? `0 0 0 1px ${colors.PRIMARY}` : 'none'};
  `
}

/** Same footprint as the seed badge it replaces, so rows stay aligned. */
const logoStyle = (isMatch: boolean) => css`
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: 50%;
  /* contain, not cover — crests are often non-square and must not be cropped */
  object-fit: contain;
  /* Keeps a wide crest off the circle's edge, as the panel and grid crests do. */
  padding: 1px;
  background: ${colors.SECONDARY_200};
  box-shadow: ${isMatch ? `0 0 0 1px ${colors.PRIMARY}` : 'none'};
`

const labelButton = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const nameStyle = (isSelected: boolean, isMatch: boolean) => css`
  ${paragraphSmall}
  ${isSelected || isMatch ? weightSemiBold : weightMedium}
  color: ${isMatch ? colors.PRIMARY : colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`

const metaStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_100};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-variant-numeric: tabular-nums;
`

export default TreeNodeRow
